import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, FileText, Search, Globe, MapPin, Languages, Tags, FolderKanban } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { tasksApi, serpApi, projectsApi, keywordGroupsApi, type Project, type AnalysisAccountProfile, type KeywordGroup } from "@/api";
import { LOCATION_OPTIONS, LANGUAGE_OPTIONS, DEFAULT_LOCATION, DEFAULT_LANGUAGE } from "@/lib/serp-constants";

export function QuickAddButton() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  // ── Shared state ──
  const [projectId, setProjectId] = useState("none");
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectAccount, setNewProjectAccount] = useState("__system_default__");
  const [groupId, setGroupId] = useState("none");
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [locationCode, setLocationCode] = useState(DEFAULT_LOCATION.toString());
  const [languageCode, setLanguageCode] = useState(DEFAULT_LANGUAGE);

  // ── Content tab state ──
  const [url, setUrl] = useState("");
  const [contentKeyword, setContentKeyword] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [contentPriority, setContentPriority] = useState("normal");
  const [batchUrls, setBatchUrls] = useState("");
  const [batchContentKeyword, setBatchContentKeyword] = useState("");
  const [batchPriority, setBatchPriority] = useState("normal");

  // ── Keyword tab state ──
  const [serpKeyword, setSerpKeyword] = useState("");
  const [serpTargetUrl, setSerpTargetUrl] = useState("");
  const [serpProductService, setSerpProductService] = useState("");
  const [batchKeywords, setBatchKeywords] = useState("");
  const [batchTargetUrl, setBatchTargetUrl] = useState("");
  const [batchProductService, setBatchProductService] = useState("");

  // ── Data queries ──
  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ["projects"],
    queryFn: async () => { const r = await projectsApi.list(); return r.data || []; },
    enabled: open,
  });

  const { data: groups = [] } = useQuery<KeywordGroup[]>({
    queryKey: ["keyword-groups", projectId],
    queryFn: async () => {
      if (!projectId || projectId === "none") return [];
      return (await keywordGroupsApi.list(projectId)).data || [];
    },
    enabled: open && projectId !== "none",
  });

  const { data: serpStatus } = useQuery({
    queryKey: ["serp-status"],
    queryFn: async () => { const r = await serpApi.status(); return r.data; },
    enabled: open,
  });

  const { data: accountProfiles = [] } = useQuery<AnalysisAccountProfile[]>({
    queryKey: ["analysis-account-profiles"],
    queryFn: async () => (await projectsApi.analysisAccountProfiles()).data || [],
    enabled: open,
  });

  const selectedProject = projects.find(project => project.project_id === projectId);
  const selectedProfile = accountProfiles.find(profile => selectedProject?.analysis_account_profile_key ? profile.accountKey === selectedProject.analysis_account_profile_key : profile.isDefault === true);
  const accountLabel = selectedProject ? (selectedProfile ? (selectedProfile.accountKey === "company" ? "Company" : "Personal") : "Unavailable") : (selectedProfile ? "System Default" : "Unavailable");
  const llmLabel = selectedProfile?.llmProfileKey === "cometapi_company" ? "CometAPI" : selectedProfile?.llmProfileKey === "openai_personal" ? "OpenAI" : "Unavailable";
  const analysisReady = selectedProfile?.dataforseoConfigured === true && selectedProfile.llmConfigured === true;

  // ── Mutations ──
  const createProjectMutation = useMutation({
    mutationFn: () => projectsApi.create({
      name: newProjectName.trim(),
      analysis_account_profile_key: newProjectAccount === "__system_default__" ? null : newProjectAccount as "personal" | "company",
    }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      const newId = res.data?.project_id;
      if (newId) setProjectId(newId);
      toast.success(`專案「${newProjectName.trim()}」已建立`);
      setNewProjectName(""); setNewProjectAccount("__system_default__"); setShowNewProject(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const createGroupMutation = useMutation({
    mutationFn: () => keywordGroupsApi.create({ project_id: projectId, name: newGroupName.trim() }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["keyword-groups", projectId] });
      const newId = res.data?.group_id;
      if (newId) setGroupId(newId);
      toast.success(`群組「${newGroupName.trim()}」已建立`);
      setNewGroupName(""); setShowNewGroup(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Content mutations
  const createTaskMutation = useMutation({
    mutationFn: () => tasksApi.create({
      url: url.trim(),
      keyword: contentKeyword.trim() || undefined,
      project_id: projectId !== "none" ? projectId : undefined,
      priority: contentPriority === "high" ? 1 : undefined,
      webhook_url: webhookUrl.trim() || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      toast.success("內容解析任務已提交");
      setUrl(""); setContentKeyword(""); setWebhookUrl("");
      saveKeywordsToGroup([contentKeyword.trim()].filter(Boolean));
      setOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const batchCreateMutation = useMutation({
    mutationFn: (urls: string[]) => tasksApi.createBatch({
      urls,
      keyword: batchContentKeyword.trim() || undefined,
      project_id: projectId !== "none" ? projectId : undefined,
      priority: batchPriority === "high" ? 1 : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      toast.success("批次內容解析已提交");
      setBatchUrls(""); setBatchContentKeyword("");
      setOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // SERP mutations
  const serpMutation = useMutation({
    mutationFn: () => serpApi.analyze({
      keyword: serpKeyword.trim(),
      location_code: parseInt(locationCode),
      language_code: languageCode,
      project_id: projectId !== "none" ? projectId : undefined,
      target_url: serpTargetUrl.trim() || undefined,
      auto_scrape: true,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serp-requests"] });
      queryClient.invalidateQueries({ queryKey: ["serp-stats"] });
      toast.success(`SERP 分析已提交：${serpKeyword}`);
      saveKeywordsToGroup([serpKeyword.trim()]);
      setSerpKeyword(""); setSerpTargetUrl(""); setSerpProductService("");
      setOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const batchSerpMutation = useMutation({
    mutationFn: (keywords: string[]) => serpApi.batchAnalyze({
      keywords,
      location_code: parseInt(locationCode),
      language_code: languageCode,
      project_id: projectId !== "none" ? projectId : undefined,
      target_url: batchTargetUrl.trim() || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serp-requests"] });
      queryClient.invalidateQueries({ queryKey: ["serp-stats"] });
      const kws = batchKeywords.split("\n").map(k => k.trim()).filter(Boolean);
      toast.success(`批次 SERP 分析已提交 (${kws.length} 個)`);
      saveKeywordsToGroup(kws);
      setBatchKeywords(""); setBatchTargetUrl(""); setBatchProductService("");
      setOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Save keywords to group (if group selected)
  const saveKeywordsToGroup = async (keywords: string[]) => {
    if (groupId === "none" || keywords.length === 0) return;
    const tags = tagInput.split(",").map(t => t.trim()).filter(Boolean);
    try {
      await keywordGroupsApi.addItems(groupId, { keywords, tags: tags.length ? tags : undefined });
      queryClient.invalidateQueries({ queryKey: ["keyword-group"] });
    } catch { /* silent — keyword saving is optional */ }
  };

  // ── Handlers ──
  const handleContentSingle = () => {
    if (!url.trim()) { toast.error("請輸入 URL"); return; }
    createTaskMutation.mutate();
  };

  const handleContentBatch = () => {
    const urls = batchUrls.split("\n").map(u => u.trim()).filter(Boolean);
    if (urls.length === 0) { toast.error("請輸入至少一個 URL"); return; }
    if (urls.length > 200) { toast.error("最多 200 個 URL"); return; }
    batchCreateMutation.mutate(urls);
  };

  const handleSerpSingle = () => {
    if (!serpKeyword.trim()) { toast.error("請輸入關鍵字"); return; }
    serpMutation.mutate();
  };

  const handleSerpBatch = () => {
    const kws = batchKeywords.split("\n").map(k => k.trim()).filter(Boolean);
    if (kws.length === 0) { toast.error("請輸入至少一個關鍵字"); return; }
    if (kws.length > 200) { toast.error("最多 200 個關鍵字"); return; }
    batchSerpMutation.mutate(kws);
  };

  const urlCount = batchUrls.split("\n").filter(u => u.trim()).length;
  const kwCount = batchKeywords.split("\n").filter(k => k.trim()).length;
  const pid = projectId !== "none" ? projectId : undefined;

  // ── Shared sub-components ──
  const ProjectSection = () => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5"><FolderKanban className="h-3.5 w-3.5" />專案 *</Label>
        <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs"
          onClick={() => { setShowNewProject(!showNewProject); setShowNewGroup(false); }}>
          {showNewProject ? "選擇現有" : "+ 建立新專案"}
        </Button>
      </div>
      {showNewProject ? (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input placeholder="新專案名稱" value={newProjectName} onChange={e => setNewProjectName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && newProjectName.trim()) createProjectMutation.mutate(); }} />
            <Button size="sm" disabled={!newProjectName.trim() || createProjectMutation.isPending}
              onClick={() => createProjectMutation.mutate()}>
              {createProjectMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "建立"}
            </Button>
          </div>
          <Select value={newProjectAccount} onValueChange={setNewProjectAccount}>
            <SelectTrigger><SelectValue placeholder="分析帳戶" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__system_default__">系統預設</SelectItem>
              {accountProfiles.map(profile => (
                <SelectItem key={profile.accountKey} value={profile.accountKey} disabled={!profile.dataforseoConfigured || !profile.llmConfigured}>
                  {profile.accountKey === "personal" ? "Personal（DataForSEO + OpenAI）" : "Company（DataForSEO + CometAPI）"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <Select value={projectId} onValueChange={v => { setProjectId(v); setGroupId("none"); }}>
          <SelectTrigger><SelectValue placeholder="請選擇專案" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">不指定專案</SelectItem>
            {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </div>
  );

  const GroupTagSection = () => {
    if (projectId === "none") return null;
    return (
      <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-1.5 text-xs"><Tags className="h-3.5 w-3.5" />關鍵字群組（選填）</Label>
            <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs"
              onClick={() => setShowNewGroup(!showNewGroup)}>
              {showNewGroup ? "選擇現有" : "+ 新增群組"}
            </Button>
          </div>
          {showNewGroup ? (
            <div className="flex gap-2">
              <Input className="h-8 text-sm" placeholder="新群組名稱" value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && newGroupName.trim()) createGroupMutation.mutate(); }} />
              <Button size="sm" className="h-8" disabled={!newGroupName.trim() || createGroupMutation.isPending}
                onClick={() => createGroupMutation.mutate()}>
                {createGroupMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "建立"}
              </Button>
            </div>
          ) : (
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="不存入群組" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">不存入群組</SelectItem>
                {groups.map(g => (
                  <SelectItem key={g.group_id} value={g.group_id}>
                    {g.name} ({g.keyword_count} 個)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {groupId !== "none" && (
          <div className="space-y-1">
            <Label className="text-xs">Tags（逗號分隔，選填）</Label>
            <Input className="h-8 text-sm" placeholder="例如：購買意圖, 品牌, 長尾"
              value={tagInput} onChange={e => setTagInput(e.target.value)} />
            <p className="text-[11px] text-muted-foreground">分析完成後，關鍵字會自動存入選定群組並加上 Tag</p>
          </div>
        )}
      </div>
    );
  };

  const LocationLanguageRow = () => (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label className="text-xs flex items-center gap-1"><MapPin className="h-3 w-3" />地區</Label>
        <Select value={locationCode} onValueChange={setLocationCode}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LOCATION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value.toString()}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs flex items-center gap-1"><Languages className="h-3 w-3" />語系</Label>
        <Select value={languageCode} onValueChange={setLanguageCode}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LANGUAGE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <>
      {/* Floating button — fixed bottom-right */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center"
        title="快速新增 SEO 分析"
      >
        <Plus className="h-6 w-6" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />新增 SEO 分析
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              網頁解析（手動貼網址批量爬取）或關鍵字搜尋（DataForSEO SERP 排名）
            </p>
          </DialogHeader>

          <Tabs defaultValue="keyword">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="content" className="flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" />網頁解析
              </TabsTrigger>
              <TabsTrigger value="keyword" className="flex items-center gap-1.5">
                <Search className="h-3.5 w-3.5" />關鍵字搜尋
              </TabsTrigger>
            </TabsList>

            {/* ══════ Content Analysis ══════ */}
            <TabsContent value="content" className="mt-3">
              <Tabs defaultValue="c-single">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="c-single">單一網址</TabsTrigger>
                  <TabsTrigger value="c-batch">批次輸入</TabsTrigger>
                </TabsList>

                <TabsContent value="c-single" className="space-y-3 pt-3">
                  <ProjectSection />
                  <div className="space-y-1">
                    <Label className="text-xs">優先級</Label>
                    <Select value={contentPriority} onValueChange={setContentPriority}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">一般 — 加入佇列依序處理</SelectItem>
                        <SelectItem value="high">高優先 — 立即執行</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">網址 *</Label>
                    <Input placeholder="https://example.com/page" value={url} onChange={e => setUrl(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">目標關鍵字（選填）</Label>
                    <Input placeholder="相關關鍵字" value={contentKeyword} onChange={e => setContentKeyword(e.target.value)} />
                  </div>
                  <GroupTagSection />
                  <div className="space-y-1">
                    <Label className="text-xs">Webhook 回呼網址（選填）</Label>
                    <Input placeholder="https://your-webhook.com/callback" value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} />
                  </div>
                  <Button onClick={handleContentSingle} className="w-full" disabled={createTaskMutation.isPending}>
                    {createTaskMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />提交中...</> : "開始分析"}
                  </Button>
                </TabsContent>

                <TabsContent value="c-batch" className="space-y-3 pt-3">
                  <ProjectSection />
                  <div className="space-y-1">
                    <Label className="text-xs">優先級</Label>
                    <Select value={batchPriority} onValueChange={setBatchPriority}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">一般 — 加入佇列依序處理</SelectItem>
                        <SelectItem value="high">高優先 — 立即執行</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">網址清單 *</Label>
                      <span className="text-[11px] text-muted-foreground">{urlCount} 個有效網址（最多 200 個）</span>
                    </div>
                    <Textarea rows={5} className="font-mono text-sm"
                      placeholder={"每行一個網址，例如：\nhttps://example.com/page1\nhttps://example.com/page2\nhttps://example.com/page3"}
                      value={batchUrls} onChange={e => setBatchUrls(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">目標關鍵字（選填）（共用）</Label>
                    <Input placeholder="例如：SEO 優化" value={batchContentKeyword} onChange={e => setBatchContentKeyword(e.target.value)} />
                  </div>
                  <GroupTagSection />
                  <Button onClick={handleContentBatch} className="w-full" disabled={batchCreateMutation.isPending}>
                    {batchCreateMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />提交中...</> : `批次分析 (${urlCount} 個)`}
                  </Button>
                </TabsContent>
              </Tabs>
            </TabsContent>

            {/* ══════ Keyword Search (SERP) ══════ */}
            <TabsContent value="keyword" className="mt-3">
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                <Badge variant={analysisReady ? "default" : "destructive"}>{accountLabel}</Badge>
                <span className="text-muted-foreground">DataForSEO: {selectedProfile?.dataforseoConfigured ? "已就緒" : "不可用"}</span>
                <span className="text-muted-foreground">LLM: {selectedProfile?.llmConfigured ? `${llmLabel} 已就緒` : `${llmLabel} 不可用`}</span>
              </div>

              <Tabs defaultValue="s-single">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="s-single">單一分析</TabsTrigger>
                  <TabsTrigger value="s-batch">批量任務</TabsTrigger>
                </TabsList>

                <TabsContent value="s-single" className="space-y-3 pt-3">
                  <div className="space-y-1">
                    <Label className="text-xs">關鍵字 *</Label>
                    <Input placeholder="輸入要分析的關鍵字" value={serpKeyword}
                      onChange={e => setSerpKeyword(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleSerpSingle()} />
                  </div>
                  <LocationLanguageRow />
                  <ProjectSection />
                  <GroupTagSection />
                  <div className="space-y-1">
                    <Label className="text-xs">目標網址（選填）</Label>
                    <Input placeholder="https://your-website.com" value={serpTargetUrl} onChange={e => setSerpTargetUrl(e.target.value)} />
                    <p className="text-[11px] text-muted-foreground">追蹤此網址在各關鍵字的排名</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">產品/服務分類（選填）</Label>
                    <Input placeholder="例如：SEO 服務、電商平台" value={serpProductService} onChange={e => setSerpProductService(e.target.value)} />
                  </div>
                  <Button onClick={handleSerpSingle} className="w-full"
                    disabled={serpMutation.isPending || !analysisReady}>
                    {serpMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />處理中...</> : "搜尋分析"}
                  </Button>
                </TabsContent>

                <TabsContent value="s-batch" className="space-y-3 pt-3">
                  <ProjectSection />
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">關鍵字清單 *</Label>
                      <span className="text-[11px] text-muted-foreground">{kwCount} 個關鍵字（最多 200 個）</span>
                    </div>
                    <Textarea rows={5} className="font-mono text-sm"
                      placeholder={"每行一個關鍵字，例如：\nisolation gowns\nmedical isolation gown\ndisposable isolation gown"}
                      value={batchKeywords} onChange={e => setBatchKeywords(e.target.value)} />
                  </div>
                  <LocationLanguageRow />
                  <GroupTagSection />
                  <div className="space-y-1">
                    <Label className="text-xs">目標網址（選填）（共用）</Label>
                    <Input placeholder="https://your-website.com" value={batchTargetUrl} onChange={e => setBatchTargetUrl(e.target.value)} />
                    <p className="text-[11px] text-muted-foreground">所有關鍵字將使用相同的目標網址進行 SEO 分析</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">產品/服務分類（選填）（共用）</Label>
                    <Input placeholder="例如：SEO 服務、電商平台" value={batchProductService} onChange={e => setBatchProductService(e.target.value)} />
                  </div>
                  <Button onClick={handleSerpBatch} className="w-full"
                    disabled={batchSerpMutation.isPending || !analysisReady}>
                    {batchSerpMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />處理中...</> : `開始批量分析 (${kwCount} 個)`}
                  </Button>
                </TabsContent>
              </Tabs>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
